import { Drawer as AntdDrawer } from 'antd';
import type { ReactNode } from 'react';

import type { ButtonVariant } from '@/components/common/button';

import { OverlayChrome, OverlayFooter } from '../overlay';

import styles from './drawer.module.scss';

export type DrawerProps = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  width?: number | string;
  maskClosable?: boolean;
  closeOnEsc?: boolean;
  loading?: boolean;
  children: ReactNode;
  primaryText?: string;
  onPrimary?: () => void;
  primaryVariant?: ButtonVariant;
  primaryDanger?: boolean;
  cancelText?: string;
  onCancel?: () => void;
  /** Internal: used by FormDrawer to wrap body + footer in a <form>. */
  onSubmit?: React.ComponentProps<'form'>['onSubmit'];
  /** Internal: makes the primary footer button type="submit". */
  primaryIsSubmit?: boolean;
};

export function Drawer({
  open,
  onClose,
  title,
  width = 464,
  maskClosable = true,
  closeOnEsc = true,
  loading = false,
  children,
  primaryText,
  onPrimary,
  primaryVariant = 'neutral',
  primaryDanger,
  cancelText = 'Cancel',
  onCancel,
  onSubmit,
  primaryIsSubmit = false,
}: DrawerProps) {
  const footer = (
    <OverlayFooter
      primaryText={primaryText}
      onPrimary={onPrimary}
      isSubmit={primaryIsSubmit}
      primaryVariant={primaryVariant}
      primaryDanger={primaryDanger}
      loading={loading}
      cancelText={cancelText}
      onCancel={onCancel ?? onClose}
    />
  );

  return (
    <AntdDrawer
      open={open}
      onClose={onClose}
      placement="right"
      width={width}
      mask={{ closable: maskClosable }}
      keyboard={closeOnEsc}
      closable={false}
      title={null}
      footer={null}
      destroyOnHidden
      className={styles.drawer}
      classNames={{ body: styles.bodyReset }}
    >
      <OverlayChrome title={title} onClose={onClose} footer={footer} onSubmit={onSubmit}>
        {children}
      </OverlayChrome>
    </AntdDrawer>
  );
}
